/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ActionLabel,
  INPUT_EMAILS,
  InputGroup,
  WEBHOOKS_METHOD,
  WEBHOOKS_URL,
} from "../../utils/constants";
import { actionApplicationId } from "../../utils/record-list-identity";
import type {
  Application,
  Member,
  Team,
  IntegrationDescription,
  InputDescription,
  OutputDescription,
} from "@smartsuite/automation-engine-api";
import {
  findInputAndPick,
  pickMemberIdFromMemberEmail,
  pickOutputIdFromReference,
  pickTeamIdFromTeamEmails,
  pickValueConfigString,
  pickValueConfigStringArray,
  pickValueDataString,
} from "../../utils/inputs";
import type { ActionCandidate } from "../../types/candidate";
import { isValueExist } from "../../services/isValueExist";
import { valueConfigsVToDescriptionString } from "./helper";
import type { PhraseDirectory } from "./helper";
import { OperatorDescriptionKinds } from "../../containers/AutomationForm/Forms/InputForm/FieldEditor/FieldEditorRow/containers/UpdateValueDropdown/constanst";

export type ActionsFormatter = (
  actions: ActionCandidate,
  applications: Application[],
  outputList: OutputDescription[],
  members: Member[],
  teams: Team[],
  integrations: IntegrationDescription[],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
) => { [key: string]: any } | string;

const isActionLabel = (actionId: string): actionId is ActionLabel =>
  Object.values(ActionLabel).includes(actionId as ActionLabel);

const getFieldsSetText = (
  action: ActionCandidate,
  outputList: OutputDescription[],
  /** Same reason as conditionsText: a member/team field value is stored as an
   * id-only value function and renders empty without the directory
   * (STR-14493). */
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  const inputsWithValue = action.inputs.filter(
    (input) =>
      isValueExist(input) ||
      input.operatorConfig?.operator.oneofKind ===
        OperatorDescriptionKinds.CLEAR,
  );

  if (inputsWithValue.length > 0) {
    let firstReturnExist = false;
    return inputsWithValue.map((input) => {
      const inputDescription = action.description.inputs.find(
        (desc) => desc.inputId === input.inputId,
      );

      if (inputDescription === undefined) {
        return "";
      }

      if (
        inputDescription.display?.display.oneofKind !== "group" ||
        inputDescription.display?.display.group.groupId !==
          InputGroup.fieldsGroup
      ) {
        return "";
      }

      if (input.value?.values === undefined) {
        return "";
      }

      const inputValue = valueConfigsVToDescriptionString(
        input.value.values,
        outputList,
        directory,
      );

      if (!firstReturnExist) {
        firstReturnExist = true;
        return getActionDataForInput(
          true,
          inputDescription,
          inputValue,
          input.operatorConfig?.operator.oneofKind,
        );
      }
      return getActionDataForInput(
        false,
        inputDescription,
        inputValue,
        input.operatorConfig?.operator.oneofKind,
      );
    });
  }

  return "";
};

const getSmartDocTextDependsOnOperator = (
  isFirst: boolean,
  inputDescription: InputDescription,
  inputValue: { [key: string]: any } | string[],
  operatorType:
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined,
): string | { [key: string]: any } => {
  switch (operatorType) {
    case "clear":
      return isFirst
        ? {
            key: "phrase-builder.action-and-clear",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-clear",
            data: { fieldName: inputDescription.label },
          };
    case "append":
      return isFirst
        ? {
            key: "phrase-builder.action-and-smartdoc-append",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-smartdoc-append",
            data: { fieldName: inputDescription.label },
          };
    case "mathAdd":
    case "mathSubtract":
    case "mathMultiply":
    case "mathDivide":
      return isFirst
        ? {
            key: "phrase-builder.action-and-calculate",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-calculate",
            data: { fieldName: inputDescription.label },
          };
    case "replace":
    case undefined:
    default:
      return isFirst
        ? {
            key: "phrase-builder.action-and-smartdoc-set",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-smartdoc-set",
            data: { fieldName: inputDescription.label },
          };
  }
};

const getTextDependOnOperator = (
  isFirst: boolean,
  inputDescription: InputDescription,
  inputValue: { [key: string]: any } | string[],
  operatorType:
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined,
): string | { [key: string]: any } => {
  switch (operatorType) {
    case "clear":
      return isFirst
        ? {
            key: "phrase-builder.action-and-clear",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-clear",
            data: { fieldName: inputDescription.label },
          };
    case "append":
      return isFirst
        ? {
            key: "phrase-builder.action-and-append",
            data: { fieldName: inputDescription.label, fieldValue: inputValue },
          }
        : {
            key: "phrase-builder.action-append",
            data: { fieldName: inputDescription.label, fieldValue: inputValue },
          };
    case "mathAdd":
    case "mathSubtract":
    case "mathMultiply":
    case "mathDivide":
      return isFirst
        ? {
            key: "phrase-builder.action-and-calculate",
            data: { fieldName: inputDescription.label },
          }
        : {
            key: "phrase-builder.action-calculate",
            data: { fieldName: inputDescription.label },
          };
    case "replace":
    case undefined:
    default:
      return isFirst
        ? {
            key: "phrase-builder.action-and-set",
            data: { fieldName: inputDescription.label, fieldValue: inputValue },
          }
        : {
            key: "phrase-builder.action-set",
            data: { fieldName: inputDescription.label, fieldValue: inputValue },
          };
  }
};

const getActionDataForInput = (
  isFirst: boolean,
  inputDescription: InputDescription,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  inputValue: { [key: string]: any } | string[],
  operatorType:
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  switch (inputDescription.editor?.editor.oneofKind) {
    case "checklist":
      return isFirst
        ? {
            key: "phrase-builder.action-and-checklist-create-item",
            data: { fieldName: inputDescription.label, count: inputValue },
          }
        : {
            key: "phrase-builder.action-checklist-create-item",
            data: { fieldName: inputDescription.label, count: inputValue },
          };
    case "smartdoc":
      return getSmartDocTextDependsOnOperator(
        isFirst,
        inputDescription,
        inputValue,
        operatorType,
      );
    default:
      return getTextDependOnOperator(
        isFirst,
        inputDescription,
        inputValue,
        operatorType,
      );
  }
};

const getRecipients = (
  action: ActionCandidate,
  members: Member[],
  teams: Team[],
  outputList: OutputDescription[],
): string => {
  const emailInputs =
    findInputAndPick(action.inputs, INPUT_EMAILS, pickValueConfigStringArray) ??
    [];
  const memberInputs = findInputAndPick(
    action.inputs,
    INPUT_EMAILS,
    pickMemberIdFromMemberEmail,
  );
  const teamInputs = findInputAndPick(
    action.inputs,
    INPUT_EMAILS,
    pickTeamIdFromTeamEmails,
  );
  const refInputs = findInputAndPick(
    action.inputs,
    INPUT_EMAILS,
    pickOutputIdFromReference,
  );

  const membersLabelArray =
    memberInputs?.map((memberId) => {
      const currentMember = members.find(
        (member) => member.memberId === memberId,
      );
      return (
        currentMember?.name ??
        currentMember?.email[0] ??
        "(user is not available)"
      );
    }) ?? [];

  const teamsLabelArray =
    teamInputs?.map((teamId) => {
      const currentTeam = teams.find((team) => team.teamId === teamId);
      return currentTeam?.name ?? "(team is not available)";
    }) ?? [];

  const refLabelArray =
    refInputs?.map((outputId) => {
      const currentOutput = outputList.find(
        (output) => output.outputId === outputId,
      );
      return currentOutput?.label ?? "(output is not available)";
    }) ?? [];

  return [
    ...emailInputs,
    ...membersLabelArray,
    ...teamsLabelArray,
    ...refLabelArray,
  ].join(", ");
};

const getWebhookUrl = (action: ActionCandidate): string => {
  const method =
    findInputAndPick(action.inputs, WEBHOOKS_METHOD, pickValueDataString) ?? "";
  const url =
    findInputAndPick(action.inputs, WEBHOOKS_URL, pickValueDataString) ?? "";

  return `${method.toUpperCase()} ${url}`;
};

// SmartSuite action formatter
export const smartsuiteActionFormatter: ActionsFormatter = (
  action: ActionCandidate,
  applications,
  outputList,
  members,
  teams,
) => {
  let applicationLabel = "App";
  const actionId = action.actionReference!.actionId;
  const applicationId = actionApplicationId(action);
  const currentApplication: Application | undefined = applications.find(
    (app) => app.id === applicationId,
  );
  if (currentApplication !== undefined) {
    applicationLabel = currentApplication.label;
  }

  const fieldsSetText = getFieldsSetText(action, outputList, {
    members,
    teams,
  });

  if (isActionLabel(actionId)) {
    if (actionId === ActionLabel.createRecord) {
      return {
        key: "phrase-builder.action-create-record",
        data: { applicationLabel, fieldsSet: fieldsSetText },
      };
    }
    if (actionId === ActionLabel.updateRecord) {
      return {
        key: "phrase-builder.action-update-record",
        data: { applicationLabel, fieldsSet: fieldsSetText },
      };
    }
    if (actionId === ActionLabel.deleteRecord) {
      return {
        key: "phrase-builder.action-delete-record",
        data: { applicationLabel, fieldsSet: fieldsSetText },
      };
    }
    if (
      actionId === ActionLabel.sendEmail ||
      actionId === ActionLabel.sendEmailFromSmartsuite
    ) {
      const recipients = getRecipients(action, members, teams, outputList);
      return {
        key: "phrase-builder.action-send-mail",
        data: { recipients },
      };
    }
    if (actionId === ActionLabel.sendNotification) {
      const recipients = getRecipients(action, members, teams, outputList);
      return {
        key: "phrase-builder.action-send-notification",
        data: { recipients },
      };
    }
    if (actionId === ActionLabel.findRecords) {
      return {
        key: "phrase-builder.action-find-records",
        data: { applicationLabel },
      };
    }
    if (actionId === ActionLabel.mergeRecords) {
      return {
        key: "phrase-builder.action-merge-records",
        data: { applicationLabel },
      };
    }
    if (actionId === ActionLabel.generatePdf) {
      return {
        key: "phrase-builder.gen-pdf",
        data: { applicationLabel },
      };
    }
    if (actionId === ActionLabel.ai) {
      return {
        key: "phrase-builder.ai",
        data: { applicationLabel },
      };
    }
    if (actionId === ActionLabel.commentRecord) {
      return {
        key: "phrase-builder.comment-record",
        data: { applicationLabel },
      };
    }
    if (actionId === ActionLabel.sendWebhook) {
      const actionText = getWebhookUrl(action);
      return {
        key: "phrase-builder.send-webhooks",
        data: { actionText },
      };
    }
  }
  return ``;
};

export const otherActionFormatter: ActionsFormatter = (
  action: ActionCandidate,
  applications,
  outputList,
  members,
  teams,
  integrations,
) => {
  const actionLabel =
    action.description.label !== ""
      ? action.description.label
      : action.actionReference!.actionId;
  const integrationId = action.actionReference!.integrationId;
  const currentIntegration = integrations.find(
    (int) => int.integrationId === integrationId,
  );

  return {
    key: "phrase-builder.action-other-integrations",
    data: {
      integrationId: currentIntegration?.label ?? integrationId,
      actionId: actionLabel,
    },
  };
};
